import { createContext, useCallback, useContext, useMemo, useState } from "react"
import type { ReactNode } from "react"
import { ko } from "./ko.ts"
import { ja } from "./ja.ts"
import { createTranslator, type Translator } from "./translate.ts"

export type Locale = "ko" | "ja"

const DICTIONARIES: Record<Locale, Record<string, string>> = { ko, ja }
const STORAGE_KEY = "returns.locale"

type LocaleContextValue = {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: Translator
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === "ko" || stored === "ja") return stored
  } catch {
    // 시크릿 창이나 저장소가 막힌 환경. 기본값으로 간다.
  }
  return "ko"
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale)

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // 저장하지 못해도 이번 세션 동안은 바뀐 언어로 쓴다.
    }
    document.documentElement.lang = next
  }, [])

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, t: createTranslator(DICTIONARIES[locale]) }),
    [locale, setLocale],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useI18n(): LocaleContextValue {
  const value = useContext(LocaleContext)
  if (!value) throw new Error("LocaleProvider 안에서만 쓸 수 있습니다")
  return value
}

/** 문구만 필요할 때. */
export function useT(): Translator {
  return useI18n().t
}
